import { findParentNodeClosestToPos } from '@tiptap/core';
import { TableCell, TableHeader } from '@tiptap/extension-table';
import { CellSelection } from '@tiptap/pm/tables';

// `@tiptap/extension-table@3.5.2`'s `TableCell`/`TableHeader` nodes declare
// only `colspan`/`rowspan`/`colwidth` - there is no cell-shading support to configure. This adds a
// `backgroundColor` attribute directly on the `td`/`th` element itself (not a `textStyle` mark), so
// shading survives structural table operations (row/column add-delete, merge) the same way
// `colwidth` does, and renders through the shared table CSS contract (and therefore the PDF) as a
// plain element style rather than an inline text-span highlight.
const backgroundColorAttribute = {
  default: null,
  // Read from the raw `style` attribute rather than `element.style.backgroundColor`, which is the
  // CSSOM's serialisation and hands back `rgb(255, 0, 0)` for an authored `#FF0000`. This keeps the
  // fill exactly as it was written, so opening and saving a page does not rewrite its markup. It is
  // the same approach - and the same reason - as the `color`/`backgroundColor` attributes in
  // `@tiptap/extension-text-style`, which the font-colour and highlight controls parse through.
  // Swatch matching is a display concern and canonicalises separately, in `TipTapColorControl`.
  //
  parseHTML: (element) => {
    // The CSSOM stays the validator even though it is no longer the source of the value: it leaves
    // `backgroundColor` empty for a declaration it cannot parse, so anything invalid is dropped
    // here rather than stored and then written back out as a bare `style=""`. It being set also
    // means there is a `style` attribute to read below.
    if (!element.style.backgroundColor) {
      return null;
    }

    const declarations = element
      .getAttribute('style')
      .split(';')
      .map((declaration) => declaration.trim())
      .filter(Boolean);

    // Walked backwards because merging nested elements can leave several `background-color`
    // declarations on one cell, and the cascade applies the last.
    for (let index = declarations.length - 1; index >= 0; index -= 1) {
      const [property, ...value] = declarations[index].split(':');

      if (value.length && property.trim().toLowerCase() === 'background-color') {
        return value.join(':').trim().replace(/['"]+/g, '');
      }
    }

    // Reached when the fill was authored as the `background` shorthand, which the walk does not
    // look for but the CSSOM has already resolved for us.
    return element.style.backgroundColor;
  },
  renderHTML: (attributes) => {
    if (!attributes.backgroundColor) {
      return {};
    }
    return {
      style: `background-color: ${attributes.backgroundColor}`
    };
  }
};

const CELL_TABLE_ROLES = ['cell', 'header_cell'];

const cellsInSelection = (selection) => {
  if (selection instanceof CellSelection) {
    const cells = [];

    selection.forEachCell((node, pos) => cells.push({ node, pos }));

    return cells;
  }

  const cell = findParentNodeClosestToPos(selection.$from, (node) =>
    CELL_TABLE_ROLES.includes(node.type.spec.tableRole)
  );

  return cell ? [{ node: cell.node, pos: cell.pos }] : [];
};

const applyBackgroundColorToSelectedCells =
  (color) =>
  ({ state, tr, dispatch }) => {
    const cellsToUpdate = cellsInSelection(state.selection).filter(
      ({ node }) => node.attrs.backgroundColor !== color
    );

    if (!cellsToUpdate.length) {
      return false;
    }

    if (dispatch) {
      // `setNodeMarkup` preserves node size, so updating one cell never shifts the positions of the
      // cells still to be updated.
      cellsToUpdate.forEach(({ node, pos }) => {
        tr.setNodeMarkup(pos, null, { ...node.attrs, backgroundColor: color });
      });
    }

    return true;
  };

export const TableCellBackgroundColor = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent(),
      backgroundColor: backgroundColorAttribute
    };
  },

  // Defined on the cell only, not also on the header, or the two extensions would register the
  // same command name twice. The commands work off the selection rather than this node type, so
  // they cover `th` cells as well.
  addCommands() {
    return {
      setCellBackgroundColor: (color) => applyBackgroundColorToSelectedCells(color),

      // null rather than '' - `renderHTML` above emits no style attribute for a falsy value, so the
      // fill is removed rather than left as an empty declaration.
      unsetCellBackgroundColor: () => applyBackgroundColorToSelectedCells(null)
    };
  }
});

export const TableHeaderBackgroundColor = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent(),
      backgroundColor: backgroundColorAttribute
    };
  }
});
